import Card, { CardProps } from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { CpoResponseModel } from 'src/core/model/cpo.model';
import GroupIcon from '@mui/icons-material/Group';

import { Fingerprint, Link, Badge, Person } from '@mui/icons-material';
import { SkeletonList } from '../skeleton/skeleton-list';

type CpoCardProps = CardProps &
  Partial<CpoResponseModel> & {
    isPending?: boolean;
  };

export function CpoCard(props: Readonly<CpoCardProps>): React.JSX.Element {
  const {
    id,
    publicKey,
    did,
    endpoint,
    isPending = false,
    sx,
    ...cardProps
  } = props;

  const { t } = useTranslation();

  const cpoTranslations: Record<keyof CpoResponseModel, string> = t(
    'model.cpo',
    {
      returnObjects: true,
    },
  );

  return (
    <Card sx={{ width: 350, ...sx }} {...cardProps}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <GroupIcon color="primary" />
          </Avatar>
        }
        title={t('cpo.card.title')}
      />

      <CardContent>
        <List>
          {isPending ? (
            <SkeletonList />
          ) : (
            <>
              <ListItem>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText
                  secondary={id ?? t('cpo.card.fallback')}
                  primary={cpoTranslations.id}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <Badge />
                </ListItemIcon>
                <ListItemText
                  secondary={
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}>
                      {publicKey ?? t('cpo.card.fallback')}
                    </Typography>
                  }
                  primary={cpoTranslations.publicKey}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <Fingerprint />
                </ListItemIcon>
                <ListItemText
                  primary={cpoTranslations.did}
                  secondary={
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}>
                      {did ?? t('cpo.card.fallback')}
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <Link />
                </ListItemIcon>
                <ListItemText
                  primary={cpoTranslations.endpoint}
                  secondary={endpoint ?? t('cpo.card.fallback')}
                />
              </ListItem>
            </>
          )}
        </List>
      </CardContent>
    </Card>
  );
}
